/**
 * Probe asset grouping (spec section 3): the words around a server's
 * Components table, shared by the customer and ops portals so both say the
 * same thing. Numbers come from AssetComponentsDto; nothing here sums rows.
 */
import type { EffectiveSystemFmv } from '../dto/asset-components';
import { formatDate, formatUsd } from './format';

export function acceleratorTotalText(f: {
  acceleratorTotal: number | null;
  pricedCount: number;
  totalCount: number;
}): string {
  const coverage = `${f.pricedCount} of ${f.totalCount} priced`;
  if (f.acceleratorTotal === null) return `Accelerator total: not priced (${coverage})`;
  return `Accelerator total: ${formatUsd(f.acceleratorTotal)} (${coverage})`;
}

/** `deltaFromSystem` = acceleratorTotal - System FMV (contract gap G5). */
export function systemDeltaText(deltaFromSystem: number | null): string | null {
  if (deltaFromSystem === null) return null;
  if (deltaFromSystem === 0) return 'Equal to System FMV';
  const amount = formatUsd(Math.abs(deltaFromSystem));
  return deltaFromSystem > 0 ? `${amount} above System FMV` : `${amount} below System FMV`;
}

/** Customer DTOs never carry bounds (578/582 doctrine), so this is ops-only in practice. */
export function componentFmvRangeText(fmv: { lowUsd: number | null; highUsd: number | null }): string | null {
  if (fmv.lowUsd === null || fmv.highUsd === null) return null;
  return `${formatUsd(fmv.lowUsd)} to ${formatUsd(fmv.highUsd)}`;
}

/**
 * SIL-1290: the caption next to the effective System FMV amount while the
 * chassis SKU is unmatched. `null` for a chassis snapshot (no caption needed
 * — the value speaks for itself) or when nothing is priced at all.
 */
export function effectiveSystemFmvLabel(f: EffectiveSystemFmv | null): string | null {
  if (!f || f.source === 'snapshot') return null;
  return f.partial ? 'components only so far, chassis not yet priced' : 'components only, chassis not yet priced';
}

/**
 * SIL-1290: display floor for System FMV / the top FMV tile while the
 * chassis SKU is unmatched — the amount and its caption combined into one
 * line. `null` means nothing is priced yet (the original "not yet priced"
 * copy still applies at the call site).
 */
export function effectiveSystemFmvText(f: EffectiveSystemFmv | null): string | null {
  if (!f) return null;
  const label = effectiveSystemFmvLabel(f);
  return label ? `${formatUsd(f.valueUsd)} · ${label}` : formatUsd(f.valueUsd);
}

/** D4: a missing component is only marked; a person confirms the removal. */
export function notSeenSinceText(lastSeen: string | null): string {
  return lastSeen ? `Not seen since ${formatDate(lastSeen)}` : 'Not seen in the latest reading';
}
